// src/utils/debugLogger.ts
/**
 * Debug logging utility for SeekTRU web portal
 * Provides similar functionality to mobile emulator logging
 */

enum LogLevel {
    DEBUG = 'DEBUG',
    INFO = 'INFO',
    WARN = 'WARN',
    ERROR = 'ERROR'
  }
  
  // Default to INFO in production, DEBUG in development
  const DEFAULT_LEVEL = process.env.NODE_ENV === 'development' ? LogLevel.DEBUG : LogLevel.INFO;
  
  // Singleton logger class
  class DebugLogger {
    private static instance: DebugLogger;
    private logLevel: LogLevel = DEFAULT_LEVEL;
    private logHistory: string[] = [];
    private maxLogHistory: number = 100;
    private fileLog: boolean = false;
    
    private constructor() {
      // Initialize console display for development
      if (process.env.NODE_ENV === 'development') {
        console.log('%c SeekTRU Debug Logger Initialized ', 'background: #2196F3; color: white; padding: 2px 6px; border-radius: 2px;');
      }
      
      // Listen for special debug commands
      window.addEventListener('keydown', (e) => {
        // Ctrl+Alt+D to show debug overlay
        if (e.ctrlKey && e.altKey && e.key === 'd') {
          this.toggleDebugOverlay();
        }
      });
      
      // Add to window for console access
      (window as any).debugLogger = this;
    }
    
    public static getInstance(): DebugLogger {
      if (!DebugLogger.instance) {
        DebugLogger.instance = new DebugLogger();
      }
      return DebugLogger.instance;
    }
    
    public setLogLevel(level: LogLevel): void {
      this.logLevel = level;
      this.debug(`Log level set to ${level}`);
    }
    
    public debug(message: string, data?: any): void {
      if (this.shouldLog(LogLevel.DEBUG)) {
        this.logWithLevel('DEBUG', message, data);
      }
    }
    
    public info(message: string, data?: any): void {
      if (this.shouldLog(LogLevel.INFO)) {
        this.logWithLevel('INFO', message, data);
      }
    }
    
    public warn(message: string, data?: any): void {
      if (this.shouldLog(LogLevel.WARN)) {
        this.logWithLevel('WARN', message, data);
      }
    }
    
    public error(message: string, error?: any): void {
      if (this.shouldLog(LogLevel.ERROR)) {
        this.logWithLevel('ERROR', message, error);
        
        // Add stack trace for errors if available
        if (error && error.stack) {
          console.error('Stack trace:', error.stack);
          this.addToHistory(`Stack: ${error.stack.split('\n')[0]}`);
        }
      }
    }
    
    // Track a function execution with timing
    public track<T>(name: string, fn: () => T): T {
      const start = performance.now();
      try {
        const result = fn();
        const end = performance.now();
        this.debug(`${name} completed in ${(end - start).toFixed(2)}ms`);
        return result;
      } catch (error) {
        const end = performance.now();
        this.error(`${name} failed after ${(end - start).toFixed(2)}ms`, error);
        throw error;
      }
    }
    
    // Async version of track
    public async trackAsync<T>(name: string, fn: () => Promise<T>): Promise<T> {
      const start = performance.now();
      try {
        const result = await fn();
        const end = performance.now();
        this.debug(`${name} completed in ${(end - start).toFixed(2)}ms`);
        return result;
      } catch (error) {
        const end = performance.now();
        this.error(`${name} failed after ${(end - start).toFixed(2)}ms`, error);
        throw error;
      }
    }
    
    // Get logs for display
    public getLogs(): string[] {
      return [...this.logHistory];
    }
    
    // Clear log history
    public clearLogs(): void {
      this.logHistory = [];
      this.debug('Logs cleared');
    }
    
    // Toggle debug overlay
    private toggleDebugOverlay(): void {
      let overlay = document.getElementById('debug-overlay');
      
      if (overlay) {
        overlay.remove();
        return;
      }
      
      // Create new overlay
      overlay = document.createElement('div');
      overlay.id = 'debug-overlay';
      overlay.style.position = 'fixed';
      overlay.style.bottom = '0';
      overlay.style.right = '0';
      overlay.style.width = '400px';
      overlay.style.height = '300px';
      overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
      overlay.style.color = 'white';
      overlay.style.padding = '10px';
      overlay.style.zIndex = '9999';
      overlay.style.overflowY = 'auto';
      overlay.style.fontFamily = 'monospace';
      overlay.style.fontSize = '12px';
      
      // Create close button - Fixed TypeScript error with null check
      const closeButton = document.createElement('button');
      closeButton.textContent = 'Close';
      closeButton.style.position = 'absolute';
      closeButton.style.right = '10px';
      closeButton.style.top = '10px';
      
      // Fixed TS error: add null check before using overlay.remove()
      closeButton.onclick = () => {
        const overlayElement = document.getElementById('debug-overlay');
        if (overlayElement) {
          overlayElement.remove();
        }
      };
      
      // Create clear button - Fixed TypeScript error with this reference
      const clearButton = document.createElement('button');
      clearButton.textContent = 'Clear Logs';
      clearButton.style.position = 'absolute';
      clearButton.style.right = '70px';
      clearButton.style.top = '10px';
      
      // Store reference to this for the onclick handler
      const self = this;
      clearButton.onclick = function() {
        self.clearLogs();
      };
      
      const logContainer = document.createElement('div');
      logContainer.style.marginTop = '40px';
      
      const updateLogs = () => {
        logContainer.innerHTML = '';
        this.logHistory.forEach(log => {
          const logLine = document.createElement('div');
          logLine.textContent = log;
          logContainer.appendChild(logLine);
        });
        
        // Auto-scroll to bottom
        logContainer.scrollTop = logContainer.scrollHeight;
      };
      
      // Update initially
      updateLogs();
      
      overlay.appendChild(closeButton);
      overlay.appendChild(clearButton);
      overlay.appendChild(logContainer);
      
      document.body.appendChild(overlay);
      
      this.debug('Debug overlay opened');
    }
    
    // Enable or disable file logging
    public enableFileLogging(enable: boolean): void {
      this.fileLog = enable;
      this.debug(`File logging ${enable ? 'enabled' : 'disabled'}`);
    }
    
    // Helper method to check if we should log at the given level
    private shouldLog(level: LogLevel): boolean {
      const levels = Object.values(LogLevel);
      return levels.indexOf(level) >= levels.indexOf(this.logLevel);
    }
    
    // Helper method to log with the appropriate level
    private logWithLevel(level: string, message: string, data?: any): void {
      const timestamp = new Date().toISOString();
      const logMessage = `[${timestamp}] [${level}] ${message}`;
      
      // Add to history
      this.addToHistory(logMessage);
      
      // Console output with appropriate method
      switch (level) {
        case 'DEBUG':
          console.debug(logMessage, data !== undefined ? data : '');
          break;
        case 'INFO':
          console.info(logMessage, data !== undefined ? data : '');
          break;
        case 'WARN':
          console.warn(logMessage, data !== undefined ? data : '');
          break;
        case 'ERROR':
          console.error(logMessage, data !== undefined ? data : '');
          break;
      }
      
      // File logging if enabled (could implement writing to IndexedDB or localStorage)
      if (this.fileLog) {
        // Implement file logging mechanism
      }
    }
    
    // Add to history with limit
    private addToHistory(message: string): void {
      this.logHistory.push(message);
      
      // Trim if exceeds limit
      if (this.logHistory.length > this.maxLogHistory) {
        this.logHistory = this.logHistory.slice(-this.maxLogHistory);
      }
    }
  }
  
  // Pre-configured logger instance
  export const logger = DebugLogger.getInstance();
  
  // Export types for TypeScript
  export { LogLevel };
  
  // Default export
  export default logger;